import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';

/**
 * 게시글 작성/조회 화면(page-newpost.js, page-editpost.js, page-post.js)과
 * 전역 사이드 위젯(widget-ai-assistant.js)을 잇는 최소 Context.
 * PostActionBarContext.js와 동일한 패턴: 페이지가 마운트 중일 때만 항목을 게시(publish)하고,
 * 언마운트되면 스스로 정리(clear)한다.
 */
const PostDraftContext = createContext(null);

export function PostDraftProvider({ children }) {
  const [entry, setEntry] = useState(null);

  const publish = useCallback((next) => setEntry(next), []);
  const clear = useCallback((token) => {
    setEntry((current) => (current && current.token === token ? null : current));
  }, []);

  return (
    <PostDraftContext.Provider value={{ entry, publish, clear }}>
      {children}
    </PostDraftContext.Provider>
  );
}

function usePostDraftContext() {
  const ctx = useContext(PostDraftContext);
  if (!ctx) throw new Error('usePostDraftContext는 PostDraftProvider 내부에서만 사용할 수 있습니다.');
  return ctx;
}

let tokenSeq = 0;

/**
 * 게시글 작성/조회 페이지가 자신의 초안 상태를 위젯에 노출할 때 쓰는 훅.
 *
 * accessors는 매 렌더마다 최신 클로저를 담은 객체를 넘기면 된다(리렌더마다 새 객체여도 무방).
 * 등록 자체는 mode가 바뀌지 않는 한 다시 실행되지 않으므로, 위젯이 액션을 실행하는 시점에
 * getSnapshot()을 호출하면 항상 최신 값을 읽는다 — 매 키 입력마다 위젯을 리렌더시키지 않는다.
 *
 * @param {'edit'|'readonly'} mode
 * @param {{
 *   getTitle?: () => string,
 *   getContent?: () => string,
 *   getAbstract?: () => string,
 *   getTags?: () => string[],
 *   setTitle?: (v: string) => void,
 *   setAbstract?: (v: string) => void,
 *   appendContent?: (html: string) => void,
 *   addTags?: (tags: string[]) => void,
 * }} accessors
 * @param {boolean} [active=true] 조건부로 등록을 켜고 끄고 싶을 때(예: 게시글 소유자만 등록) 사용
 */
export function useRegisterPostDraft(mode, accessors, active = true) {
  const { publish, clear } = usePostDraftContext();
  const accessorsRef = useRef(accessors);
  accessorsRef.current = accessors;

  useEffect(() => {
    if (!active) return undefined;
    const token = ++tokenSeq;
    publish({
      token,
      mode,
      getSnapshot: () => {
        const a = accessorsRef.current;
        return {
          title: a.getTitle ? a.getTitle() : '',
          content: a.getContent ? a.getContent() : '',
          abstract: a.getAbstract ? a.getAbstract() : '',
          tags: a.getTags ? a.getTags() : [],
        };
      },
      setTitle: (v) => accessorsRef.current.setTitle?.(v),
      setAbstract: (v) => accessorsRef.current.setAbstract?.(v),
      appendContent: (html) => accessorsRef.current.appendContent?.(html),
      addTags: (tags) => accessorsRef.current.addTags?.(tags),
    });
    return () => clear(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, active, publish, clear]);
}

/** 위젯이 현재 등록된 초안(있다면)을 읽을 때 쓰는 훅. 등록된 페이지가 없으면 null. */
export function usePostDraft() {
  return usePostDraftContext().entry;
}
